import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AvailabilityDot, RiskPill, VerifiedBadge } from "@/components/Badges";
import { DoorOpen, Send, Sparkles, Users, Zap, CheckCircle2, Camera, X, Pencil, CornerDownRight, Reply } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { uploadImage, useAuthedImage } from "@/lib/files";
import { AnnotateDialog } from "@/components/AnnotateDialog";

export default function Room() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [askMode, setAskMode] = useState("both");
  const [aiStreaming, setAiStreaming] = useState(false);
  const [streamBuf, setStreamBuf] = useState("");
  const [resolveOpen, setResolveOpen] = useState(false);
  const [status, setStatus] = useState("solved");
  const [saveKnowledge, setSaveKnowledge] = useState(true);
  const [publishPublic, setPublishPublic] = useState(false);
  const [consent, setConsent] = useState(false);
  const [feedbackTags, setFeedbackTags] = useState([]);
  const [kudosStep, setKudosStep] = useState(null); // {helpers:[{user}], selected, amount, message}
  const [pendingPhoto, setPendingPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [annotate, setAnnotate] = useState({ open: false, path: null });
  const [replyTo, setReplyTo] = useState(null); // message being replied to
  const fileRef = useRef(null);
  const replyFileRef = useRef(null);
  const scrollRef = useRef(null);
  const pendingSrc = useAuthedImage(pendingPhoto);

  const messagesById = messages.reduce((acc, m) => { acc[m.message_id] = m; return acc; }, {});

  const openAnnotate = (path) => setAnnotate({ open: true, path });
  const onAnnotationDone = async ({ path }) => {
    try {
      const { data } = await api.post(`/rooms/${id}/messages`, {
        content: "marked up the photo",
        kind: "annotation",
        attachment_url: path,
        annotation_of: annotate.path,
      });
      setMessages((m) => [...m, data]);
    } catch { toast.error("Send failed"); }
  };

  const startReply = (m) => {
    setReplyTo(m);
    setTimeout(() => replyFileRef.current?.click(), 0);
  };
  const onReplyPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !replyTo) { e.target.value = ""; return; }
    setUploading(true);
    try {
      const { path } = await uploadImage(f);
      const { data } = await api.post(`/rooms/${id}/messages`, {
        content: "(follow-up photo)",
        kind: "image",
        attachment_url: path,
        reply_to: replyTo.message_id,
      });
      setMessages((m) => [...m, data]);
      setReplyTo(null);
      toast.success("Follow-up photo posted");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  useEffect(() => {
    (async () => {
      try {
        await api.post(`/rooms/${id}/join`);
        const { data: r } = await api.get(`/rooms/${id}`);
        setRoom(r);
      } catch {
        toast.error("Could not load room");
      }
    })();
  }, [id]);

  useEffect(() => {
    let timer;
    const poll = async () => {
      try {
        const since = messages.length > 0 ? messages[messages.length - 1].created_at : null;
        const { data } = await api.get(`/rooms/${id}/messages${since ? `?since=${encodeURIComponent(since)}` : ""}`);
        if (data.length > 0) setMessages((m) => {
          const seen = new Set(m.map((x) => x.message_id));
          const fresh = data.filter((x) => !seen.has(x.message_id));
          return fresh.length ? [...m, ...fresh] : m;
        });
      } catch {}
      timer = setTimeout(poll, 2500);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [id, messages.length]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streamBuf]);

  const onPickPhoto = () => fileRef.current?.click();
  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const { path } = await uploadImage(f);
      setPendingPhoto(path);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const send = async () => {
    if (!input.trim() && !pendingPhoto) return;
    const content = input.trim() || "(photo)";
    const attach = pendingPhoto;
    setInput("");
    setPendingPhoto(null);
    try {
      const { data } = await api.post(`/rooms/${id}/messages`, {
        content,
        kind: attach ? "image" : "text",
        attachment_url: attach || null,
      });
      setMessages((m) => [...m, data]);
      if (askMode !== "people") streamAI(content, attach);
    } catch {
      toast.error("Send failed");
    }
  };

  const streamAI = (prompt, image) => {
    setAiStreaming(true);
    setStreamBuf("");
    const url = `${API_BASE}/rooms/${id}/ai/stream?prompt=${encodeURIComponent(prompt)}&model=auto${image ? `&image=${encodeURIComponent(image)}` : ""}&token=${encodeURIComponent(localStorage.getItem("cling_token") || "")}`;
    const es = new EventSource(url, { withCredentials: true });
    es.onmessage = (ev) => {
      try {
        const d = JSON.parse(ev.data);
        if (d.type === "delta") setStreamBuf((t) => t + d.content);
        if (d.type === "done") {
          setAiStreaming(false);
          setStreamBuf("");
          es.close();
        }
      } catch {}
    };
    es.onerror = () => { setAiStreaming(false); es.close(); };
  };

  const resolve = async () => {
    try {
      // Capture the Immediate Help hold before finalizing the request
      const req = await api.get(`/help-requests/${room.request_id}`).then(r => r.data).catch(() => null);
      const imm = req?.immediate_help;
      if (imm && imm.session_id && imm.status === "authorized" && status === "solved") {
        try {
          await api.post(`/payments/immediate/${imm.session_id}/capture`);
          toast.success("Session captured — helper has been paid");
        } catch (e) {
          toast.error("Could not capture payment: " + (e?.response?.data?.detail || "unknown"));
          return;
        }
      } else if (imm && imm.session_id && imm.status === "authorized" && status !== "solved") {
        // Not solved — void the hold so no funds are captured
        try { await api.post(`/payments/immediate/${imm.session_id}/cancel`); } catch {}
      }
      const { data } = await api.post(`/help-requests/${room.request_id}/resolve`, {
        status,
        feedback: feedbackTags,
        save_as_knowledge: saveKnowledge,
        publish_public: publishPublic && consent,
        consent: consent,
      });
      setResolveOpen(false);
      toast.success(status === "solved" ? "Marked as solved" : "Marked");
      // Offer Kudos to human helpers in the room (not owner, not AI)
      const humanHelpers = (room.member_details || []).filter(
        (m) => m.user_id !== user?.user_id && m.user_id !== room.owner_id
      );
      if (status === "solved" && humanHelpers.length > 0) {
        setKudosStep({ helpers: humanHelpers, selected: humanHelpers[0], amount: 0, message: "", kc: data.knowledge_case });
      } else if (data.knowledge_case) {
        nav(`/knowledge/${data.knowledge_case.case_id}`);
      } else {
        nav("/home");
      }
    } catch {
      toast.error("Could not resolve");
    }
  };

  const sendKudos = async () => {
    if (!kudosStep?.selected) return;
    // Free (amount = 0) — record instantly. Paid — route through Stripe Checkout.
    if (!kudosStep.amount || kudosStep.amount <= 0) {
      try {
        await api.post("/kudos", {
          recipient_id: kudosStep.selected.user_id,
          room_id: room.room_id,
          request_id: room.request_id,
          message: kudosStep.message,
          amount: 0,
        });
        toast.success(`Kudos sent to ${kudosStep.selected.name}`);
        const kc = kudosStep.kc;
        setKudosStep(null);
        if (kc) nav(`/knowledge/${kc.case_id}`); else nav("/home");
      } catch { toast.error("Could not send Kudos"); }
      return;
    }
    try {
      const { data } = await api.post("/payments/kudos/checkout", {
        recipient_id: kudosStep.selected.user_id,
        amount: kudosStep.amount,
        message: kudosStep.message,
        room_id: room.room_id,
        request_id: room.request_id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open Stripe checkout");
    }
  };

  if (!room) return <div className="min-h-screen"><Navbar /><div className="p-10 text-center text-muted-foreground">Loading room…</div></div>;
  const isOwner = user?.user_id === room.owner_id;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1 flex flex-col max-w-6xl w-full mx-auto px-4 sm:px-6 py-4">
        <div className="door-glow border-2 border-[color:hsl(var(--amber))]/60 bg-gradient-to-r from-[color:hsl(var(--amber))]/10 to-[color:hsl(var(--teal))]/10 rounded-2xl p-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <DoorOpen className="w-5 h-5 text-[color:hsl(var(--amber))]" />
            <div>
              <div className="text-xs font-mono-label text-[color:hsl(var(--teal))]">Live · {room.category} · {room.member_details?.length || 1} inside</div>
              <div className="font-semibold text-sm sm:text-base">{room.title}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <RiskPill risk={room.risk} />
            {isOwner && (
              <Button size="sm" className="btn-teal rounded-full" onClick={() => setResolveOpen(true)} data-testid="resolve-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Resolve
              </Button>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-4 mt-4 flex-1 min-h-0">
          <aside className="hidden lg:block border rounded-2xl bg-card p-4 h-fit">
            <div className="text-xs font-mono-label text-[color:hsl(var(--teal))] flex items-center gap-1"><Users className="w-3 h-3" /> In this room</div>
            <div className="mt-3 space-y-2">
              {room.member_details?.map((m) => (
                <div key={m.user_id} className="flex items-center gap-2">
                  <Avatar className="h-7 w-7 border"><AvatarImage src={m.picture} /><AvatarFallback className="text-[10px] bg-[color:hsl(var(--teal))] text-white">{m.name?.[0]}</AvatarFallback></Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 text-sm truncate">{m.name}<AvailabilityDot status={m.availability} /></div>
                    <VerifiedBadge level={m.verification_level} />
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                <div className="h-7 w-7 rounded-full grid place-items-center bg-gradient-to-br from-[color:hsl(var(--teal))] to-[color:hsl(var(--amber))] text-white text-[10px] font-bold">AI</div>
                <div className="text-sm">CLING AI</div>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-3 border rounded-2xl bg-card flex flex-col min-h-[60vh]">
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-list">
              {messages.length === 0 && !aiStreaming && (
                <div className="text-center text-sm text-muted-foreground py-10">
                  This room is open. Share your progress, ask a question, or wait for a helper to join.
                </div>
              )}
              {messages.map((m) => (
                <Message
                  key={m.message_id}
                  m={m}
                  me={user?.user_id}
                  onAnnotate={openAnnotate}
                  onReply={startReply}
                  parent={m.reply_to ? messagesById[m.reply_to] : null}
                />
              ))}
              {aiStreaming && streamBuf && (
                <div className="flex gap-2 items-start">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[color:hsl(var(--teal))] to-[color:hsl(var(--amber))] text-white text-xs grid place-items-center font-semibold">AI</div>
                  <div className="ai-bubble rounded-2xl rounded-tl-sm px-4 py-2 text-sm max-w-[80%] whitespace-pre-wrap">
                    <div className="font-mono-label text-[10px] text-[color:hsl(var(--teal))] mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> CLING AI</div>
                    {streamBuf}<span className="animate-pulse">▌</span>
                  </div>
                </div>
              )}
            </div>
            <div className="border-t p-3">
              {pendingPhoto && pendingSrc && (
                <div className="mb-2 relative inline-block" data-testid="room-photo-preview">
                  <img src={pendingSrc} alt="attached" className="rounded-xl border max-h-32" />
                  <button
                    onClick={() => setPendingPhoto(null)}
                    className="absolute -top-2 -right-2 bg-background border rounded-full p-1 shadow"
                    aria-label="Remove photo"
                    data-testid="room-photo-remove"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-mono-label text-muted-foreground">ASK</span>
                <div className="inline-flex rounded-full border p-0.5 bg-secondary/50">
                  {[{k:"people",l:"People",I:Users},{k:"ai",l:"AI",I:Sparkles},{k:"both",l:"Both",I:Zap}].map(({k,l,I}) => (
                    <button key={k} onClick={() => setAskMode(k)} data-testid={`room-ask-${k}`} className={`px-3 py-1 rounded-full text-xs inline-flex items-center gap-1 ${askMode===k?"bg-[color:hsl(var(--teal))] text-white":"text-muted-foreground"}`}>
                      <I className="w-3 h-3" /> {l}
                    </button>
                  ))}
                </div>
                <div className="flex-1" />
                <button
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
                  onClick={onPickPhoto}
                  disabled={uploading}
                  data-testid="room-photo-btn"
                  type="button"
                >
                  <Camera className="w-4 h-4" /> {uploading ? "Uploading…" : "Photo"}
                </button>
                <input ref={fileRef} type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="room-photo-input" />
                <input ref={replyFileRef} type="file" accept="image/*" onChange={onReplyPhoto} className="hidden" data-testid="room-reply-input" />
              </div>
              <div className="flex gap-2">
                <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" className="h-11" data-testid="room-input" />
                <Button onClick={send} className="btn-teal rounded-full h-11 px-4" data-testid="room-send"><Send className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>How did it go?</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              {[{k:"solved",l:"✅ Solved"},{k:"partial",l:"🤔 Partial"},{k:"unresolved",l:"❌ Still stuck"}].map(({k,l}) => (
                <button key={k} onClick={() => setStatus(k)} className={`border rounded-xl p-3 text-sm ${status===k?"border-[color:hsl(var(--teal))] bg-[color:hsl(var(--teal))]/10":""}`} data-testid={`resolve-${k}`}>{l}</button>
              ))}
            </div>
            <div>
              <Label>Quick feedback (optional)</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {["Helpful","Knowledgeable","Good teacher","Would ask again"].map((t) => (
                  <button key={t} onClick={() => setFeedbackTags(ft => ft.includes(t) ? ft.filter(x=>x!==t) : [...ft, t])} className={`text-xs rounded-full border px-3 py-1 ${feedbackTags.includes(t) ? "bg-[color:hsl(var(--amber))]/20 border-[color:hsl(var(--amber))]" : ""}`}>{t}</button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={saveKnowledge} onCheckedChange={setSaveKnowledge} data-testid="save-knowledge-check" />
              Save as reusable knowledge (helps future CLING users)
            </label>
            {saveKnowledge && (
              <div className="rounded-xl border border-[color:hsl(var(--amber))]/40 bg-[color:hsl(var(--amber))]/5 p-3 space-y-2" data-testid="publish-public-row">
                <label className="flex items-start gap-2 text-sm">
                  <Checkbox checked={publishPublic} onCheckedChange={setPublishPublic} data-testid="publish-public-check" />
                  <span><b>Allow an anonymized public Solution Page.</b> A sanitized version (no names, emails, phones, addresses, or private photos) will be discoverable so others with the same problem can benefit. Your Help Room stays private.</span>
                </label>
                {publishPublic && (
                  <label className="flex items-start gap-2 text-sm pl-6" data-testid="consent-row">
                    <Checkbox checked={consent} onCheckedChange={setConsent} data-testid="consent-check" />
                    <span>I confirm the participants in this room agreed to publishing a sanitized version.</span>
                  </label>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResolveOpen(false)}>Cancel</Button>
            <Button onClick={resolve} className="btn-teal" data-testid="resolve-submit">Finish</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AnnotateDialog
        open={annotate.open}
        onOpenChange={(v) => setAnnotate((a) => ({ ...a, open: v }))}
        sourcePath={annotate.path}
        onDone={onAnnotationDone}
      />

      <Dialog open={!!kudosStep} onOpenChange={(v) => !v && setKudosStep(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send Kudos 🙌</DialogTitle></DialogHeader>
          {kudosStep && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Thank the person who helped you. Kudos are always optional.
              </div>
              {kudosStep.helpers.length >= 1 && (
                <div className={`grid ${kudosStep.helpers.length > 1 ? "grid-cols-2" : "grid-cols-1"} gap-2`}>
                  {kudosStep.helpers.map((h) => (
                    <button
                      key={h.user_id}
                      onClick={() => setKudosStep((s) => ({ ...s, selected: h }))}
                      className={`border rounded-xl p-2 text-left flex items-center gap-2 ${kudosStep.selected?.user_id === h.user_id ? "border-[color:hsl(var(--teal))] bg-[color:hsl(var(--teal))]/10" : ""}`}
                      data-testid={`kudos-helper-${h.user_id}`}
                    >
                      <Avatar className="h-8 w-8"><AvatarImage src={h.picture} /><AvatarFallback className="text-[10px] bg-[color:hsl(var(--teal))] text-white">{h.name?.[0]}</AvatarFallback></Avatar>
                      <div className="min-w-0"><div className="text-sm font-medium truncate">{h.name}</div><div className="text-[10px] text-muted-foreground truncate">{h.bio}</div></div>
                    </button>
                  ))}
                </div>
              )}
              <div>
                <Label>Amount (optional)</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[0, 2, 5, 10].map((a) => (
                    <button
                      key={a}
                      onClick={() => setKudosStep((s) => ({ ...s, amount: a }))}
                      className={`rounded-full border px-4 py-1.5 text-sm ${kudosStep.amount === a ? "bg-[color:hsl(var(--amber))] text-[color:hsl(var(--amber-foreground))] border-[color:hsl(var(--amber))]" : ""}`}
                      data-testid={`kudos-amount-${a}`}
                    >
                      {a === 0 ? "Just say thanks" : `$${a}`}
                    </button>
                  ))}
                </div>
                {kudosStep.amount > 0 && (
                  <div className="mt-2 text-[11px] text-muted-foreground" data-testid="kudos-pending-note">
                    Powered by Stripe. Test mode — use card 4242 4242 4242 4242, any future expiry, any CVC.
                  </div>
                )}
              </div>
              <div>
                <Label>Message (optional)</Label>
                <Input
                  className="mt-1"
                  value={kudosStep.message}
                  onChange={(e) => setKudosStep((s) => ({ ...s, message: e.target.value }))}
                  placeholder="Thanks — you saved me a service call!"
                  data-testid="kudos-message"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { const kc = kudosStep?.kc; setKudosStep(null); if (kc) nav(`/knowledge/${kc.case_id}`); else nav("/home"); }} data-testid="kudos-skip">Skip</Button>
            <Button onClick={sendKudos} className="btn-teal" data-testid="kudos-send">Send Kudos</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Message({ m, me, onAnnotate, onReply, parent }) {
  if (m.sender_kind === "system") {
    return <div className="text-center text-[11px] font-mono-label text-muted-foreground">{m.content}</div>;
  }
  const isMe = m.sender_id === me;
  const isAI = m.sender_kind === "ai";
  const isAnnotation = m.kind === "annotation";
  return (
    <div className={`flex gap-2 items-start ${isMe ? "flex-row-reverse" : ""}`}>
      {isAI ? (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[color:hsl(var(--teal))] to-[color:hsl(var(--amber))] text-white text-xs grid place-items-center font-semibold shrink-0">AI</div>
      ) : (
        <Avatar className="h-8 w-8 border shrink-0">
          <AvatarImage src={m.sender_picture} />
          <AvatarFallback className="text-[10px] bg-[color:hsl(var(--teal))] text-white">{m.sender_name?.[0]}</AvatarFallback>
        </Avatar>
      )}
      <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap ${isAI ? "ai-bubble rounded-tl-sm" : isMe ? "bg-[color:hsl(var(--teal))] text-white rounded-tr-sm" : "human-bubble rounded-tl-sm"}`}>
        {!isMe && (
          <div className={`font-mono-label text-[10px] mb-0.5 ${isAI ? "text-[color:hsl(var(--teal))]" : "text-muted-foreground"}`}>
            {isAI ? <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> {m.sender_name}</span> : m.sender_name}
          </div>
        )}
        {parent && <ReplyRef parent={parent} isMe={isMe} />}
        {isAnnotation && (
          <div className="font-mono-label text-[10px] text-[color:hsl(var(--amber))] mb-1 inline-flex items-center gap-1">
            <Pencil className="w-3 h-3" /> Marked up
          </div>
        )}
        {m.attachment_url && (
          <MessageImage
            path={m.attachment_url}
            onAnnotate={onAnnotate}
            onReply={isAnnotation ? () => onReply?.(m) : null}
          />
        )}
        {m.content && m.content !== "(photo)" && m.content !== "(follow-up photo)" ? m.content : null}
      </div>
    </div>
  );
}

function ReplyRef({ parent, isMe }) {
  const src = useAuthedImage(parent.attachment_url);
  return (
    <div
      className={`mb-2 flex items-center gap-2 rounded-lg border-l-2 pl-2 py-1 ${isMe ? "border-white/60 bg-white/10" : "border-[color:hsl(var(--amber))] bg-[color:hsl(var(--amber))]/5"}`}
      data-testid={`reply-ref-${parent.message_id}`}
    >
      <CornerDownRight className={`w-3 h-3 ${isMe ? "text-white/80" : "text-[color:hsl(var(--amber))]"}`} />
      {src && <img src={src} alt="parent" className="w-10 h-10 rounded object-cover border" />}
      <div className="min-w-0">
        <div className={`text-[10px] font-mono-label truncate ${isMe ? "text-white/70" : "text-muted-foreground"}`}>Replying to {parent.sender_name}</div>
        <div className={`text-[11px] truncate ${isMe ? "text-white/90" : ""}`}>
          {parent.kind === "annotation" ? "Marked up photo" : parent.kind === "image" ? "Photo" : parent.content?.slice(0, 60) || "message"}
        </div>
      </div>
    </div>
  );
}

function MessageImage({ path, onAnnotate, onReply }) {
  const src = useAuthedImage(path);
  if (!src) return <div className="mb-1 h-32 w-48 rounded-xl bg-secondary animate-pulse" />;
  return (
    <div className="relative group mb-1">
      <img src={src} alt="attachment" className="rounded-xl max-h-64 border" />
      <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
        {onReply && (
          <button
            onClick={onReply}
            data-testid={`reply-btn-${path}`}
            className="inline-flex items-center gap-1 rounded-full bg-[color:hsl(var(--amber))] hover:brightness-110 text-[color:hsl(var(--amber-foreground))] text-[11px] font-semibold px-2.5 py-1"
          >
            <Reply className="w-3 h-3" /> Reply with photo
          </button>
        )}
        {onAnnotate && (
          <button
            onClick={() => onAnnotate(path)}
            data-testid={`annotate-btn-${path}`}
            className="inline-flex items-center gap-1 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-sm text-white text-[11px] font-medium px-2.5 py-1"
          >
            <Pencil className="w-3 h-3" /> Annotate
          </button>
        )}
      </div>
    </div>
  );
}
